import {
  WAF_MATCH_OPERATORS,
  WAF_MATCH_TARGETS,
  type WafControlSpec,
  type WafMatchCondition,
} from './prevention.schema';

export const DEFAULT_NETWORK_SOURCE_CIDR = '0.0.0.0/0';

export const DEFAULT_PROTECTED_CIDRS = [
  '127.0.0.0/8',
  '169.254.0.0/16',
  '100.64.0.0/16',
  '192.0.0.0/24',
  '198.18.0.0/15',
] as const;

const MIN_IPV4_PREFIX = 24;
const MIN_IPV6_PREFIX = 64;

const BROAD_NETWORK_LITERALS = new Set([
  '0.0.0.0',
  '0.0.0.0/0',
  '::',
  '::/0',
  '*',
  'any',
]);

const BROAD_WAF_PATHS = new Set(['/*', '*', '/', '**']);
const SHORT_GENERIC_PATH_SEGMENTS = new Set([
  '/api',
  '/v1',
  '/v2',
  '/app',
  '/public',
]);

const CATCH_ALL_REGEX_VALUES = new Set([
  '.*',
  '.+',
  '^/',
  '^/.*',
  '^.*',
  '(?:)',
  '^$',
  '[\\s\\S]*',
  '[\\S\\s]*',
]);

const TECHNIQUE_OPERATORS = new Set<string>([
  WAF_MATCH_OPERATORS.SQLI_MATCH,
  WAF_MATCH_OPERATORS.XSS_MATCH,
]);

export type Ipv4Range = { start: number; end: number };

export const resolveEffectiveNetworkCidr = (
  cidr: string | null | undefined,
): string => {
  const trimmed = cidr?.trim();

  return trimmed && trimmed.length > 0 ? trimmed : DEFAULT_NETWORK_SOURCE_CIDR;
};

export const parseCidrEntries = (raw: string | null | undefined): string[] =>
  (raw ?? '')
    .split(/[\s,]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

export const mergeProtectedCidrs = (
  configured: readonly string[],
): string[] => [...new Set([...DEFAULT_PROTECTED_CIDRS, ...configured])];

export const isWorldOpenCidr = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();

  return (
    normalized === '0.0.0.0/0' ||
    normalized === '0.0.0.0' ||
    normalized === '::/0' ||
    normalized === '::'
  );
};

export const isOverlyBroadNetworkRange = (
  value: string | null | undefined,
): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();

  if (BROAD_NETWORK_LITERALS.has(normalized)) {
    return true;
  }

  const parsed = parseIpPrefix(normalized);

  if (!parsed) {
    return normalized.endsWith('/0');
  }

  if (parsed.version === 4) {
    return parsed.prefix < MIN_IPV4_PREFIX;
  }

  return parsed.prefix < MIN_IPV6_PREFIX;
};

export const isBroadNetworkCidr = (cidr: string | null | undefined): boolean =>
  isOverlyBroadNetworkRange(resolveEffectiveNetworkCidr(cidr));

const parseIpPrefix = (
  value: string,
): { version: 4 | 6; prefix: number } | null => {
  const [base, prefixPart] = value.split('/');

  if (!base) {
    return null;
  }

  if (base.includes(':')) {
    const prefix =
      prefixPart === undefined ? 128 : Number.parseInt(prefixPart, 10);

    if (!Number.isInteger(prefix) || prefix < 0 || prefix > 128) {
      return null;
    }

    return { version: 6, prefix };
  }

  if (toIpv4Int(base) === null) {
    return null;
  }

  const prefix =
    prefixPart === undefined ? 32 : Number.parseInt(prefixPart, 10);

  if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) {
    return null;
  }

  return { version: 4, prefix };
};

export const parseIpv4Range = (
  value: string | null | undefined,
): Ipv4Range | null => {
  if (!value) {
    return null;
  }

  const [base, prefixPart] = value.trim().split('/');
  const baseInt = toIpv4Int(base);

  if (baseInt === null) {
    return null;
  }

  if (prefixPart === undefined) {
    return { start: baseInt, end: baseInt };
  }

  const prefix = Number(prefixPart);

  if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) {
    return null;
  }

  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  const start = (baseInt & mask) >>> 0;
  const end = (start | (~mask >>> 0)) >>> 0;

  return { start, end };
};

export const ipv4RangesOverlap = (a: Ipv4Range, b: Ipv4Range): boolean =>
  a.start <= b.end && b.start <= a.end;

export const wouldLockOutProtectedRange = ({
  candidates,
  protectedCidrs,
}: {
  candidates: readonly (string | null | undefined)[];
  protectedCidrs: readonly string[];
}): boolean => {
  const protectedRanges = protectedCidrs
    .map((entry) => parseIpv4Range(entry))
    .filter((range): range is Ipv4Range => range !== null);

  if (protectedRanges.length === 0) {
    return false;
  }

  return candidates.some((candidate) => {
    const range = parseIpv4Range(candidate);

    if (!range) {
      return false;
    }

    return protectedRanges.some((protectedRange) =>
      ipv4RangesOverlap(range, protectedRange),
    );
  });
};

const toIpv4Int = (value: string | undefined): number | null => {
  if (!value) {
    return null;
  }

  const octets = value.split('.');

  if (octets.length !== 4) {
    return null;
  }

  let result = 0;

  for (const octet of octets) {
    if (!/^\d{1,3}$/.test(octet)) {
      return null;
    }

    const parsed = Number(octet);

    if (parsed > 255) {
      return null;
    }

    result = (result << 8) | parsed;
  }

  return result >>> 0;
};

const isCatchAllMatchValue = (value: string | undefined): boolean => {
  const normalized = value?.trim() ?? '';

  if (normalized.length === 0) {
    return true;
  }

  return CATCH_ALL_REGEX_VALUES.has(normalized.toLowerCase());
};

const countLiteralPathChars = (value: string): number =>
  value
    .replace(/\\./g, 'x')
    .replace(/[.*+?^${}()|[\]\\]/g, '')
    .replace(/\/+$/, '').length;

const isBroadPathCondition = (condition: WafMatchCondition): boolean => {
  if (condition.target !== WAF_MATCH_TARGETS.PATH) {
    return false;
  }

  const normalized = condition.value?.trim() ?? '';

  if (BROAD_WAF_PATHS.has(normalized)) {
    return true;
  }

  const trimmed = normalized.replace(/\/+$/, '');

  if (SHORT_GENERIC_PATH_SEGMENTS.has(trimmed)) {
    return true;
  }

  if (condition.operator === WAF_MATCH_OPERATORS.REGEX) {
    return (
      isCatchAllMatchValue(normalized) || countLiteralPathChars(normalized) < 3
    );
  }

  return trimmed.length > 0 && trimmed.length <= 2;
};

const isKeyedField = (target: WafMatchCondition['target']): boolean =>
  target === WAF_MATCH_TARGETS.HEADER ||
  target === WAF_MATCH_TARGETS.QUERY_PARAM;

const isSinkTarget = (target: WafMatchCondition['target']): boolean =>
  target === WAF_MATCH_TARGETS.BODY ||
  target === WAF_MATCH_TARGETS.QUERY_PARAM ||
  target === WAF_MATCH_TARGETS.QUERY_STRING ||
  target === WAF_MATCH_TARGETS.HEADER;

const isTechniqueMatcher = (condition: WafMatchCondition): boolean => {
  if (TECHNIQUE_OPERATORS.has(condition.operator)) {
    return true;
  }

  return (
    condition.operator === WAF_MATCH_OPERATORS.REGEX &&
    isSinkTarget(condition.target) &&
    !isCatchAllMatchValue(condition.value)
  );
};

export const isNarrowWafCondition = (condition: WafMatchCondition): boolean => {
  if (condition.target === WAF_MATCH_TARGETS.METHOD) {
    return false;
  }

  if (isTechniqueMatcher(condition)) {
    return true;
  }

  if (condition.target === WAF_MATCH_TARGETS.PATH) {
    return !isBroadPathCondition(condition);
  }

  if (isKeyedField(condition.target)) {
    return (
      Boolean(condition.key?.trim()) && !isCatchAllMatchValue(condition.value)
    );
  }

  if (condition.target === WAF_MATCH_TARGETS.QUERY_STRING) {
    return !isCatchAllMatchValue(condition.value);
  }

  if (condition.target === WAF_MATCH_TARGETS.BODY) {
    return !isCatchAllMatchValue(condition.value);
  }

  return false;
};

export const collectWafBlockBreadthViolations = (
  spec: WafControlSpec,
): string[] => {
  const hasNarrowCondition = spec.matchConditions.some(isNarrowWafCondition);

  if (hasNarrowCondition) {
    return [];
  }

  const broadPath = spec.matchConditions.find(isBroadPathCondition);
  const label = broadPath?.value ?? spec.matchConditions[0]?.value ?? '(empty)';

  return [
    `WAF block rule matches an overly broad path "${label}". Target the specific vulnerable path or a technique-level sink condition.`,
  ];
};

export { isBroadPathCondition };
